import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clientApiService, ProjectItem } from "../../services/client-api.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  planning: { label: "Planificación", className: "bg-purple-100 text-purple-700" },
  in_progress: { label: "En progreso", className: "bg-blue-100 text-blue-700" },
  on_hold: { label: "En espera", className: "bg-yellow-100 text-yellow-700" },
  completed: { label: "Completado", className: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", className: "bg-red-100 text-red-700" },
};

const TYPE_LABELS: Record<string, string> = {
  residential: "🏠 Residencial",
  commercial: "🏢 Comercial",
  industrial: "🏭 Industrial",
  remodelation: "🔨 Remodelación",
  other: "📦 Otro",
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatRelativeTime(t: number | null) {
  if (!t) return "—";
  const diff = Date.now() - t;
  const d = Math.floor(diff / 86400000);
  if (d < 1) return "Hoy";
  if (d < 7) return `Hace ${d} d`;
  return new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

export function ProjectsListPage() {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientApiService.listProjects();
      setProjects(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar proyectos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      const matchesStatus = statusFilter === "all" || p.status === statusFilter;
      const term = search.trim().toLowerCase();
      const matchesSearch =
        !term ||
        p.name.toLowerCase().includes(term) ||
        (p.address || "").toLowerCase().includes(term) ||
        (p.description || "").toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [projects, statusFilter, search]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mis Proyectos</h2>
          <p className="text-sm text-slate-500 mt-1">
            {projects.length} {projects.length === 1 ? "proyecto" : "proyectos"} en total
          </p>
        </div>
        <button
          onClick={load}
          disabled={isLoading}
          className="text-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
        >
          {isLoading ? "Actualizando..." : "🔄 Actualizar"}
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por nombre o dirección..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:border-primary outline-none text-sm"
        >
          <option value="all">Todos los estados</option>
          <option value="planning">Planificación</option>
          <option value="in_progress">En progreso</option>
          <option value="on_hold">En espera</option>
          <option value="completed">Completado</option>
          <option value="cancelled">Cancelado</option>
        </select>
      </div>

      {/* Content */}
      {isLoading && projects.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">🏗️</div>
          <h3 className="text-lg font-semibold text-slate-900">
            {projects.length === 0 ? "Aún no tienes proyectos" : "Sin resultados"}
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            {projects.length === 0
              ? "Cuando el despacho te asigne un proyecto, aparecerá aquí con toda su información."
              : "Prueba con otros filtros o términos de búsqueda."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectItem }) {
  const status = STATUS_LABELS[project.status] || {
    label: project.status,
    className: "bg-slate-100 text-slate-700",
  };

  return (
    <Link
      to={`/dashboard/projects/${project.id}`}
      className="group block bg-white rounded-2xl border border-slate-200 hover:border-primary/40 hover:shadow-lg transition overflow-hidden"
    >
      {/* Cover */}
      <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
        {project.coverImageUrl ? (
          <img
            src={project.coverImageUrl}
            alt={project.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl opacity-30">
            🏗️
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${status.className}`}>
            {status.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-slate-500">
              {TYPE_LABELS[project.projectType] || project.projectType}
            </span>
          </div>
          <h3 className="font-bold text-slate-900 truncate">{project.name}</h3>
          {project.address && (
            <p className="text-xs text-slate-500 truncate mt-0.5">📍 {project.address}</p>
          )}
        </div>

        {/* Progress */}
        <div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>Avance</span>
            <span className="font-bold text-primary">{project.progress}%</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${Math.min(project.progress, 100)}%` }}
            />
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span>💰 {formatCurrency(project.budget)}</span>
          <span>Último avance: {formatRelativeTime(project.lastAdvanceAt)}</span>
        </div>

        {/* Counters */}
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span>📈 {project.advancesCount} avances</span>
          <span>📄 {project.documentsCount} docs</span>
        </div>
      </div>
    </Link>
  );
}